import { useFacilityContext } from '@aspendental/shared-components-web';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { usePathname } from 'next/navigation';
import router from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getStepConfiguration } from './ScheduleLayout.helpers';
import TermsAndConditions from './TermsAndConditions';
import Confirmation from '@src/components/Confirmation';
import FurtherAssistanceContact from '@src/components/FurtherAssistanceContact';
import LoadingModal from '@src/components/LoadingSpinnerModal';
import PatientInformationStep from '@src/components/PatientInformationStep';
import PromptToCallModal from '@src/components/PromptToCallModal';
import QuestionnaireStep from '@src/components/QuestionnaireStep';
import ScheduleInfoModal from '@src/components/ScheduleInfoModal/ScheduleInfoModal';
import ScheduleStepProgress from '@src/components/ScheduleStepProgress';
import StepCTAs from '@src/components/StepCTAs/StepCTAs';
import TimeSlotSelectionStep from '@src/components/TimeSlotSelectionStep';
import { FormIdMappings } from '@src/constants';
import { getFormSectionQuestions } from '@src/services/getFormQuestions';
import { useAppStore, usePatientInformationStore, useStepManagerStore } from '@src/store';
import useAppointmentInformationStore from '@src/store/appointment-information.store';
import useScheduleQuestionsStore from '@src/store/schedule-questions.store';
import marketingSourceUrlParse from '@src/utils/marketing-source-url-parse';
interface IScheduleLayoutProps {
  smartyKey: string;
  baseUrl: string;
  enablePtcSlotFilter: boolean;
  enableHydra?: boolean;
}
function ScheduleLayout({
  smartyKey,
  baseUrl,
  enablePtcSlotFilter,
  enableHydra = false // main FF is ON, and preset location is within pilot group
}: Readonly<IScheduleLayoutProps>) {
  const facilityDetails = useFacilityContext();
  const {
    t
  } = useTranslation('index');
  useEffect(() => {
    marketingSourceUrlParse();
  }, []);
  const pathname = usePathname();
  const isPageTwo = pathname === '/2/';
  const {
    locale
  } = router;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const steps = isSmallScreen ? getStepConfiguration('mobile') : getStepConfiguration('desktop');
  const [modalType, setModalType] = useState<'promptToCall' | 'existingConsultation'>('promptToCall');
  const {
    loadingAppointments
  } = useAppointmentInformationStore(store => store);
  const {
    setQuestionData,
    setQuestions,
    scheduleInfoModal,
    setScheduleInfoModal,
    schedulePopupError,
    setSchedulePopupError,
    setFormId,
    setLoadingQuestions
  } = useScheduleQuestionsStore(store => store);
  const {
    leadId,
    firstName
  } = usePatientInformationStore();
  const {
    chosenAppointmentData,
    presetCenterID,
    presetCenterName,
    setPresetCenterID,
    setPresetCenterName,
    noChosenAppointmentError,
    setNoChosenAppointmentError
  } = useAppointmentInformationStore();
  const {
    setLeadFormId,
    setEnableHydraCalls,
    sessionId
  } = useAppStore(store => store);
  const {
    currentStep,
    isStepSubmitting
  } = useStepManagerStore(store => store);
  useEffect(() => {
    setEnableHydraCalls(enableHydra);
  }, [enableHydra]);
  useEffect(() => {
    // keep initial facility in state for tracking purposes
    if (facilityDetails.officeInfo && !presetCenterName && !presetCenterID) {
      setPresetCenterID(facilityDetails.officeInfo.code);
      setPresetCenterName(facilityDetails.officeInfo.name);
    }
  }, [facilityDetails, setPresetCenterID, setPresetCenterName, presetCenterName, presetCenterID]);
  useEffect(() => {
    const leadFormId = isPageTwo ? FormIdMappings.formID3DentalQuestions : FormIdMappings.formID;
    setLeadFormId(leadFormId);
    setLoadingQuestions(true);
    getFormSectionQuestions({
      form: leadFormId,
      locale: locale,
      enableHydra,
      sessionId
    }).then(data => {
      setLoadingQuestions(false);
      setQuestionData(data?.form?.sections[0]?.questions);
      setQuestions(data?.form?.sections[0]?.questions);
    }, () => {
      setLoadingQuestions(false);
    });
  }, [setQuestionData, isPageTwo, setLeadFormId, locale]);
  const mainContentRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (mainContentRef?.current) {
      const firstFocusableElement = mainContentRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      firstFocusableElement[0]?.focus();
    }
    setNoChosenAppointmentError(false);
    setFormId(`step-${currentStep}-form`);
  }, [currentStep]);
  useEffect(() => {
    setNoChosenAppointmentError(false);
  }, [chosenAppointmentData]);
  const stepsData = useMemo(() => {
    const previousSteps = steps.filter(step => step.step < currentStep);
    const currentStepData = steps.find(step => step.step === currentStep);
    const nextSteps = steps.filter(step => step.step > currentStep);
    return {
      previousSteps,
      currentStep: currentStepData,
      nextSteps
    };
  }, [currentStep, steps]);
  const stepTitle = (() => {
    if (currentStep === 4) {
      return `${t('allSet')} ${firstName}!`;
    }
    return t(steps[currentStep - 1]?.titleKey);
  })();
  return <>
			{currentStep === 2 && isStepSubmitting && <LoadingModal open={isStepSubmitting} message={t('pleaseWait')} dataTestId="loading-modal-please-wait" />}
			{currentStep === 3 && loadingAppointments && <LoadingModal open={loadingAppointments} message={t('retrievingAppointments')} dataTestId="loading-modal-retrieving-appointments" />}

			<PromptToCallModal open={schedulePopupError} handleClose={() => setSchedulePopupError(false)} modalType={modalType} data-sentry-element="PromptToCallModal" data-sentry-source-file="ScheduleLayout.tsx" />
			{Boolean(scheduleInfoModal) && <ScheduleInfoModal open={Boolean(scheduleInfoModal)} handleClose={() => setScheduleInfoModal(null)} {...scheduleInfoModal} />}
			<Stack direction="row" p={{
      md: 5,
      xs: 0,
      xl: '2% 15%',
      l: '2% 10%'
    }} bgcolor="backgrounds.neutral" spacing={2.5} justifyContent="center" data-sentry-element="Stack" data-sentry-source-file="ScheduleLayout.tsx">
				<Stack direction="row" spacing={2.5} width="100%" maxWidth="75rem" data-sentry-element="Stack" data-sentry-source-file="ScheduleLayout.tsx">
					{!isSmallScreen && <Box width={275} minWidth={275} bgcolor="backgrounds.white" py={1.5} borderRadius={2} sx={{
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'neutrals.primary',
          height: 'max-content'
        }}>
							{steps.map(({
            step,
            titleKey
          }, index) => {
            const selected = step === currentStep;
            const completed = step < currentStep;
            if (index < 3) {
              return <ScheduleStepProgress key={step} step={step} stepTitle={titleKey} selected={selected} completed={completed} disabled={!completed || currentStep === steps.length} />;
            }
          })}
						</Box>}

					<Box width="100%" ref={mainContentRef} sx={{
          pt: {
            xs: '4rem',
            md: 0
          },
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }} data-sentry-element="Box" data-sentry-source-file="ScheduleLayout.tsx">
						<Box width="100%" bgcolor="#FFFFFF" p={{
            md: 5,
            xs: 2.5
          }} borderRadius={2} sx={{
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: 'neutrals.primary'
          }} data-sentry-element="Box" data-sentry-source-file="ScheduleLayout.tsx">
							{/* Main title/header - show threeSteps on all screens except step 4 */}
							{currentStep !== 4 && <Typography variant="header2" data-test-id="schedule-layout-main-title" mb={2}>
									{t('threeSteps')}
								</Typography>}
							{/* Step title: only show on step 4 for both desktop and mobile */}
							{currentStep === 4 && <Typography variant="header2" data-test-id="schedule-layout-title" pb={1}>
									{stepTitle}
								</Typography>}
							{steps[currentStep - 1]?.subtitleKey && <Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} data-test-id="schedule-layout-subtitle">
									{t(steps[currentStep - 1].subtitleKey)}
								</Typography>}

							<Stack direction="column" spacing={1} data-sentry-element="Stack" data-sentry-source-file="ScheduleLayout.tsx">
								{isSmallScreen && <Stack direction="column">
										{/* Mobile previous steps */}
										{stepsData.previousSteps.map(({
                  step,
                  titleKey
                }) => {
                  const completed = step < currentStep;
                  return <Box key={step}>
													<ScheduleStepProgress key={step} step={step} stepTitle={titleKey} completed={true} disabled={!completed || currentStep === steps.length} />
												</Box>;
                })}
										{/* Mobile current step */}
										<ScheduleStepProgress step={currentStep} stepTitle={stepTitle} selected={true} />
									</Stack>}

								<Box pb={{
                md: 0,
                xs: 4
              }} data-sentry-element="Box" data-sentry-source-file="ScheduleLayout.tsx">
									{currentStep === 1 && <QuestionnaireStep />}
									{currentStep === 2 && <PatientInformationStep smartyKey={smartyKey} setModalType={setModalType} />}
									{currentStep === 3 && <TimeSlotSelectionStep leadId={leadId} setModalType={setModalType} enablePtcSlotFilter={enablePtcSlotFilter} />}
									{currentStep === 4 && <Confirmation enablePtcSlotFilter={enablePtcSlotFilter} />}
								</Box>
							</Stack>
						</Box>

						{!isSmallScreen && <Box mt={3.5}>
								<FurtherAssistanceContact />
							</Box>}
					</Box>
				</Stack>
			</Stack>

			{currentStep < 4 && <Stack py={2.5} alignItems="center" justifyContent="center" sx={{
      boxShadow: 'rgba(0, 0, 0, 0.1) 0px -1px 4px',
      position: currentStep === 3 ? 'sticky' : 'static',
      bottom: 0,
      zIndex: 20,
      backgroundColor: 'backgrounds.white'
    }}>
					{noChosenAppointmentError && <Box role="alert">
							<Typography variant="bodyMediumBook" color="error.dark">
								{t('chooseAnotherTime')}
							</Typography>
						</Box>}
					{currentStep === 3 && <TermsAndConditions baseUrl={baseUrl} />}

					<Stack direction="row" spacing={1.5} alignItems="center">
						{currentStep < 4 && <StepCTAs enablePtcSlotFilter={enablePtcSlotFilter} />}
					</Stack>
				</Stack>}
			{isSmallScreen && <Box my={2.5} sx={{
      textAlign: 'center'
    }}>
					<FurtherAssistanceContact />
				</Box>}
		</>;
}
export default ScheduleLayout;